import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * Cross-cutting UI chrome state.
 *
 * Only state that several unrelated parts of the app must agree on belongs
 * here — currently just the sidebar's open state (owned by the layout,
 * toggled from the header). Anything a single component can own stays local
 * to that component.
 *
 * This previously also held a `theme` preference (light/dark/system), applied
 * to the document root by `ThemeProvider`. It is intentionally gone: the app
 * is committed to light mode only, and a stored/OS-driven preference is
 * exactly what caused pages to silently render dark. See `ThemeProvider`.
 */

export interface UiState {
  /** Sidebar visibility on small viewports, where it renders as an overlay. */
  isMobileSidebarOpen: boolean;
  /** Sidebar collapsed to icons on large viewports. */
  isSidebarCollapsed: boolean;
}

const initialState: UiState = {
  isMobileSidebarOpen: false,
  isSidebarCollapsed: false,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleMobileSidebar(state) {
      state.isMobileSidebarOpen = !state.isMobileSidebarOpen;
    },
    setMobileSidebarOpen(state, action: PayloadAction<boolean>) {
      state.isMobileSidebarOpen = action.payload;
    },
    toggleSidebarCollapsed(state) {
      state.isSidebarCollapsed = !state.isSidebarCollapsed;
    },
  },
  selectors: {
    selectIsMobileSidebarOpen: (state) => state.isMobileSidebarOpen,
    selectIsSidebarCollapsed: (state) => state.isSidebarCollapsed,
  },
});

export const { toggleMobileSidebar, setMobileSidebarOpen, toggleSidebarCollapsed } =
  uiSlice.actions;

export const { selectIsMobileSidebarOpen, selectIsSidebarCollapsed } = uiSlice.selectors;

export const uiReducer = uiSlice.reducer;
